import { createContext, ReactNode, useContext, useState } from "react";
import { useTheme } from "@mui/material";
import { CvssMetric, CvssVersion, parseCvss } from "../cvss";
import { ScoreRating } from "~/operations";

export type CvssScore = {
  vector: string;
  value: number;
};

type HoveredMetric = CvssMetric | undefined;
type FocusedMetric = CvssMetric | undefined;

export type CvssRiskContextType = {
  vector: string;
  version: CvssVersion;
  score: number;
  rating: ScoreRating;
  metrics: CvssMetric[];
  focusedMetric: FocusedMetric;
  hoveredMetric: HoveredMetric;
  isFocused: (metric: CvssMetric) => boolean;
  isHovered: (metric: CvssMetric) => boolean;
  setFocusedMetric: (metric: FocusedMetric) => void;
  toggleFocusedMetric: (metric: CvssMetric) => void;
  setHoveredMetric: (metric: HoveredMetric) => void;
  toggleHoveredMetric: (metric: CvssMetric) => void;
  onMetricMouseEnter: (metric: CvssMetric) => void;
  onMetricMouseLeave: (metric: CvssMetric) => void;
  onMetricFocus: (metric: CvssMetric) => void;
  onMetricBlur: (metric: CvssMetric) => void;
  isPrintView?: boolean;
};

export const CvssRiskContext = createContext<CvssRiskContextType | undefined>(
  undefined,
);

export type CvssRiskProviderProps = {
  children: ReactNode;
  vector: string;
  isPrintView?: boolean;
};

export function CvssRiskProvider({
  children,
  vector,
  isPrintView,
}: CvssRiskProviderProps) {
  const theme = useTheme();
  const { metrics, score, rating, version } = parseCvss(vector, theme);

  const [focusedMetric, setFocusedMetric] = useState<FocusedMetric>(undefined);
  const [hoveredMetric, setHoveredMetric] = useState<HoveredMetric>(undefined);

  const onMetricMouseEnter = (metric: CvssMetric) => {
    setFocusedMetric(metric);
  };

  const onMetricMouseLeave = (_metric: CvssMetric) => {
    setFocusedMetric(undefined);
  };

  const onMetricFocus = (metric: CvssMetric) => {
    setFocusedMetric(metric);
  };

  const onMetricBlur = (_metric: CvssMetric) => {
    setFocusedMetric(undefined);
  };

  const isFocused = (metric: CvssMetric) => {
    return focusedMetric?.field.title === metric.field.title;
  };

  const isHovered = (metric: CvssMetric) => {
    return hoveredMetric?.field.title === metric.field.title;
  };

  const toggleFocusedMetric = (metric: CvssMetric) => {
    setFocusedMetric(isFocused(metric) ? undefined : metric);
  };

  const toggleHoveredMetric = (metric: CvssMetric) => {
    setHoveredMetric(isHovered(metric) ? undefined : metric);
  };

  return (
    <CvssRiskContext.Provider
      value={{
        vector,
        version,
        score,
        rating,
        metrics,
        focusedMetric,
        hoveredMetric,
        isFocused,
        isHovered,
        setFocusedMetric,
        toggleFocusedMetric,
        setHoveredMetric,
        toggleHoveredMetric,
        onMetricMouseEnter,
        onMetricMouseLeave,
        onMetricFocus,
        onMetricBlur,
        isPrintView,
      }}
    >
      {children}
    </CvssRiskContext.Provider>
  );
}

export function useCvssRisk() {
  const context = useContext(CvssRiskContext);
  if (context === undefined) {
    throw new Error("useCvssRisk must be used within a CvssRiskProvider");
  }
  return context;
}
